import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const KEY = "ba_theme";
const ThemeCtx = createContext({ theme: "light", setTheme: () => {}, toggle: () => {} });

const systemPrefersDark = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const readStored = () => {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "light" || v === "dark") return v;
  } catch { /* private mode */ }
  return null;
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored() || (systemPrefersDark() ? "dark" : "light"));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#17120f" : "#fbf6f2");
    try { localStorage.setItem(KEY, theme); } catch { /* private mode */ }
  }, [theme]);

  // Prati sistemsku postavku dok korisnik sam ne odabere temu
  useEffect(() => {
    if (readStored()) return;
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = e => setThemeState(e.matches ? "dark" : "light");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const setTheme = useCallback(v => setThemeState(v === "dark" ? "dark" : "light"), []);
  const toggle = useCallback(() => setThemeState(t => (t === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ theme, setTheme, toggle, isDark: theme === "dark" }), [theme, setTheme, toggle]);

  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}

export const useTheme = () => useContext(ThemeCtx);
